import { EventEmitter, Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormlyFormBuilder } from '@ngx-formly/core';
import {
  IFormDocument,
  IToolButtonEvent,
} from 'app/services/api5-service/api.interface';
import { IFormButtonUi } from 'components/form-button/model';
import { IFormCollapseEvent } from 'components/form-collapse/model';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';

import {
  IFilterChipsEvent,
  IFormButtonClickEvent,
  IFormModel,
  IFormWizardClickEvent,
  ILinkClickEvent,
} from './dynamic-form.interface';
import {
  ISPFieldConfig,
  ISPFieldType,
  DynamicFormContext,
  ISPValidator,
  ISPFormState,
} from './model';
import { ButtonsService } from './services/buttons.service';
import { CaptchaService } from './services/captcha.service';
import { ConditionService } from './services/condition.service';
import { DisabledService } from './services/disabled.service';
import { FormService } from './services/form.service';
import { HiddenService } from './services/hidden.service';
import {
  DYNAMIC_FORM_SCROLLABLE_CONTAINER_SELECTOR,
  LayoutService,
} from './services/layout.service';
import { ListService } from './services/list.service';
import { LoaderService } from './services/loader/loader.service';
import { MixedService } from './services/mixed-service';
import { SelectService } from './services/select.service';
import { SetValuesService } from './services/set-values.service';
import { TriggerService } from './services/trigger/trigger.service';
import { ValidationService } from './services/validation.service';
import { VisibilityService } from './services/visibility.service';
import { IListSelectEvent } from './types/list/model';
import { TemplateConfig } from './types/template/model';
import { findConfig } from './utils/formly-configs';
import { isFormSubmittable } from './utils/is-form-submitable';

/**
 * Default list dropdown width
 *
 * @WARN setted from js, it cannot be setted from css variables, cause dropdown opens out of dynamic-form css scope
 */
const LIST_DROPDOWN_DEFAULT_WIDTH = '400px';

/**
 * Field list service
 */
@UntilDestroy()
@Injectable()
export class DynamicFormService {
  private readonly reinitializeSubject = new Subject<void>();

  /** Page collapse event */
  readonly collapseEvent = new EventEmitter<IFormCollapseEvent>();

  /** Button click event */
  readonly buttonClickEvent = new EventEmitter<IFormButtonClickEvent>();

  /** Toolbar button click event */
  readonly toolbarClickEvent = new EventEmitter<IToolButtonEvent>();

  /** Wizard button click event */
  readonly wizardClickEvent = new EventEmitter<IFormWizardClickEvent>();

  /** List item select event */
  readonly listSelectEvent = new EventEmitter<IListSelectEvent>();

  /** Link click event */
  readonly linkClickEvent = new EventEmitter<ILinkClickEvent>();

  /** Reset field value event */
  readonly chipsEvent = new EventEmitter<IFilterChipsEvent>();

  /** Dropdown parent selector */
  dropdownParentSelector: string;

  /** Scroll selector for dropdown update position */
  dropdownScrollSelector: string;

  /** Whether hints should be displayed or not */
  showHints = true;

  /** Width of dropdown list for controls, that use dropdown for displaying */
  selectDropdownWidth = LIST_DROPDOWN_DEFAULT_WIDTH;

  /** Bounding element selector for validation popups */
  validationBoundingElement = DYNAMIC_FORM_SCROLLABLE_CONTAINER_SELECTOR;

  /** Is general error should be displayed in notification */
  showGeneralErrorInNotification = true;

  /** Is need using extform-layout */
  isExtformLayout = false;

  /** Is need to display fake labels. Filter case. @TODO i.ablov remove it! */
  fakeLabels = false;

  /** A function that allows you to additionally process the generated field configs */
  customHandlingConfigsFunction?: (
    configs: ISPFieldConfig[],
    state: ISPFormState,
  ) => void;

  /** Dynamic form context information */
  context: DynamicFormContext;

  /**
   * Flag to control the synchronization of the model with doc
   *
   * @TODO e.polyakov for the branding form, there is no need to synchronize with doc, remove this after BILLDr-543
   */
  needSyncModelWithDoc = true;

  constructor(
    private readonly setValuesService: SetValuesService,
    private readonly validationService: ValidationService,
    private readonly conditionService: ConditionService,
    private readonly hiddenService: HiddenService,
    private readonly disabledService: DisabledService,
    private readonly buttonsService: ButtonsService,
    private readonly mixedService: MixedService,
    private readonly listService: ListService,
    private readonly selectService: SelectService,
    private readonly layoutService: LayoutService,
    private readonly captchaService: CaptchaService,
    private readonly visibilityService: VisibilityService,
    private readonly formService: FormService,
    private readonly sanitizer: DomSanitizer,
    private readonly loaderService: LoaderService,
    private readonly triggerService: TriggerService,
    private readonly builder: FormlyFormBuilder,
  ) {}

  /**
   * Set autofocus flag to input-like field (which has error of any usual one)
   *
   * @param configs - form configs
   */
  private setAutofocus(configs: ISPFieldConfig[]): ISPFieldConfig[] {
    let specificFindCondition: (f: ISPFieldConfig) => boolean;
    if (this.formService.doc?.error) {
      // first we try to focus a field containing error from server, even if it's currently hidden by form mode (the mode is supposed to be switched a moment later by another method)
      specificFindCondition = f => {
        const hasError =
          f.validators?.[ISPValidator.ErrorFromBackend] !== undefined;
        return !f.props.isHidden && hasError;
      };
    }

    type FocusableField =
      | ISPFieldConfig<ISPFieldType.Input>
      | ISPFieldConfig<ISPFieldType.Password>
      | ISPFieldConfig<ISPFieldType.TextArea>;

    const isFieldFocusabel = (field: ISPFieldConfig): field is FocusableField =>
      field.type === ISPFieldType.Password ||
      field.type === ISPFieldType.Input ||
      field.type === ISPFieldType.TextArea;

    const fieldToFocus = findConfig(configs, (field: ISPFieldConfig) => {
      if (field.type === ISPFieldType.List) {
        return 'skip-node-and-childs';
      }

      return (
        isFieldFocusabel(field) &&
        field.props?.originalControl?.$readonly !== 'yes' &&
        specificFindCondition &&
        specificFindCondition(field)
      );
    }) as FocusableField | undefined;
    if (fieldToFocus) {
      fieldToFocus.props.autofocus = true;
    } else {
      const submitButton = this.buttonsService.getFirstSubmitButtonOnForm();
      if (submitButton) {
        submitButton.autofocus = true;
      }
    }

    return configs;
  }

  /**
   * Gets new options
   *
   * @param doc
   */
  private getFormState(doc: IFormDocument): ISPFormState {
    return {
      docSubject: new BehaviorSubject(doc),
      selectService: this.selectService,
      listService: this.listService,
      disabledService: this.disabledService,
      conditionService: this.conditionService,
      hiddenService: this.hiddenService,
      buttonsService: this.buttonsService,
      layoutService: this.layoutService,
      mixedService: this.mixedService,
      visibilityService: this.visibilityService,
      validationService: this.validationService,
      setValuesService: this.setValuesService,
      loaderService: this.loaderService,
      triggerService: this.triggerService,
      formService: this.formService,
      builder: this.builder,
      sanitizer: this.sanitizer,
      context: this.context,
      selectDropdownWidth: this.selectDropdownWidth,
      showHints: this.showHints,
      dropdownParentSelector: this.dropdownParentSelector,
      dropdownScrollSelector: this.dropdownScrollSelector,
      showGeneralErrorInNotification: this.showGeneralErrorInNotification,
      fakeLabels: this.fakeLabels,
      customHandlingConfigsFunction: this.customHandlingConfigsFunction,
      isExtformLayout: this.isExtformLayout,
    };
  }

  setNewDoc(nextDoc: IFormDocument, templates: TemplateConfig[]): void {
    /**
     * @HACK For now doc updating enable only for cart and order.service func!
     * @WARN disabled doc update because:
     * - validation works incorrectly when receiving an error for a specific field from the backend. FIXED
     * - when updating the doc for the checkbox, the wrapper will add its own label (the checkbox has its own implementation of the label)
     * - phone group independently validates its fields
     * - and etc
     */
    const prevDoc = this.formService.doc;
    if (
      (nextDoc.$func === 'cart' || nextDoc.$func.startsWith('v2.')) &&
      prevDoc &&
      !DocHelper.isDocControlsChanged(prevDoc, nextDoc)
    ) {
      this.updateDoc(nextDoc);
    } else {
      this.applyNewDoc(nextDoc, templates);
    }
  }

  /**
   * Initializes the service
   *
   * @param doc - response's document
   * @param templates - additional templates configs
   */
  applyNewDoc(doc: IFormDocument, templates: TemplateConfig[]): void {
    if (!DocHelper.isHaveMetadata(doc)) {
      if (doc.error) {
        this.validationService.handleDocError(doc.error, false);
      }
      return;
    }
    this.reinitializeSubject.next();

    this.formService.templateConfigs = templates;

    const state = this.getFormState(doc);
    this.formService.setFormGroup(new UntypedFormGroup({}));
    this.formService.setState(state);

    // @WARN typed form generated after 'setOptions', order matters!
    const typedForm = this.formService.typedForm;
    if (this.needSyncModelWithDoc) {
      this.formService.syncModelWithDoc();
    }

    this.loaderService
      .generateConfigs$(
        typedForm,
        this.formService.formState,
        this.formService.templateConfigs,
      )
      .pipe(takeUntil(this.reinitializeSubject), untilDestroyed(this))
      .subscribe(configs => {
        this.formService.setConfigs(configs);
      });

    this.setAutofocus(this.formService.configs);
  }

  updateDoc(doc: IFormDocument): void {
    this.formService.setDoc(doc);

    if (this.needSyncModelWithDoc) {
      this.formService.syncModelWithDoc();
      this.formService.formGroup.patchValue(this.formService.model);
    }
  }

  /**
   * Emits the form button click event and form's value
   *
   * @param button - clicked button
   * @param additionalParams - additional params for form submiting
   */
  async emitButtonClick(
    button: IFormButtonUi,
    additionalParams: IFormModel = {},
  ): Promise<void> {
    if (button.type === 'setvalues') {
      button.preloaderSubject.next(true);
      this.setValuesService
        .handleSetValues({
          setValuesType: button.blocking ? 'blocking' : 'yes',
          fieldName: button.name,
          additionalParams,
          sendFiles: true,
          skipFinalLock: true,
          appendModel: true,
          showError: true,
        })
        .pipe(
          finalize(() => {
            button.preloaderSubject.next(false);
          }),
        )
        .subscribe();
    } else {
      if (this.setValuesService.setValuesInProgress) {
        button.preloaderSubject.next(true);
        await this.setValuesService.setValuesOver;
        button.preloaderSubject.next(false);
      }

      const form: IFormModel = {
        ...this.mixedService.getUnmixedFormModel(),
        ...additionalParams,
      };
      const event: IFormButtonClickEvent = {
        button,
        model: form,
      };
      if (this.captchaService.isNeedToGetCaptchaForButtonClick(event)) {
        try {
          const resolvedValue = await firstValueFrom(
            this.captchaService.getCaptchaResolve$(),
          );
          this.captchaService.appendCaptchaResolvedValueToParams(
            event.model,
            resolvedValue,
          );
        } catch (e: unknown) {
          // event was rejected or captcha error
          return;
        }
      }

      this.buttonClickEvent.emit(event);
    }
  }

  /**
   * Sends the keyboard event to submit
   *
   * @param event - keyboard "Enter" key press event
   */
  submitFromKeyboard(event: KeyboardEvent): void {
    if (!isFormSubmittable(this.formService.configs)) {
      this.validationService.scrollToErrorField();
      return;
    }

    const focusElement = event.target as HTMLElement;

    const allowedTagNameList = ['input', 'formly-form'];
    const isAllowed = allowedTagNameList.includes(focusElement.localName);
    if (focusElement.localName === 'input') {
      focusElement.blur();
    }

    // @TODO i.ablov do as in core-manager/dist/skins/orion/src/App.Forms.js:532 clickButtonTrigger function
    const submitButton = this.buttonsService.getFirstSubmitButtonOnForm();
    if (!submitButton) {
      return;
    }
    const isButtonDisabled =
      submitButton.disabledSubject.value || submitButton.preloaderSubject.value;

    if ((!isAllowed && !event.ctrlKey) || isButtonDisabled) {
      return;
    }

    if (!isButtonDisabled) {
      this.emitButtonClick(submitButton);
    }
  }
}
